import { getEnvName } from "@callcenter/helpers"
import type { CallcenterSettings, UserSettings } from "@callcenter/services"

import { APPS_WITHOUT_SUPPORT_CHAT, SUPPORT_CHAT_IFRAME_ID } from "./support-chat.constants"
import type {
  MountSupportChatPayload,
  ShowSupportWebchatParams,
  SupportChatAnalyticsSettings
} from "./support-chat.types"

export const getIsExcludedApp = (): boolean =>
  APPS_WITHOUT_SUPPORT_CHAT.some((key) => Boolean(document.querySelector(`[data-app="${key}"]`)))

export const getIsTrial = (user: UserSettings, callcenter: CallcenterSettings): boolean =>
  Boolean(user.is_admin && callcenter.self_service_trial && !callcenter.self_service_trial_expired)

export const getIsPremium = (user: UserSettings, callcenter: CallcenterSettings): boolean =>
  Boolean(user.support_chat_enabled && callcenter.premium_support_enabled)

export const getSupportChatAnalyticsSettings = (
  user: UserSettings,
  callcenter: CallcenterSettings
): SupportChatAnalyticsSettings => {
  const { hostname } = window.location

  if (
    user.is_support ||
    getEnvName(hostname) !== "production" ||
    !REACT_APP_PUBLIC_POSTHOG_KEY ||
    !REACT_APP_PUBLIC_POSTHOG_HOST
  ) {
    return {}
  }

  return {
    posthog_key: REACT_APP_PUBLIC_POSTHOG_KEY,
    posthog_host: REACT_APP_PUBLIC_POSTHOG_HOST,
    hostname,
    uuid: callcenter.uuid,
    name: callcenter.name,
    account_level: callcenter.account_level,
    user_id: String(user.id)
  }
}

export const isShowSupportWebchat = ({
  resources,
  callcenter,
  user,
  permissions,
  whiteLabel,
  isMounted,
  isExcludedApp
}: ShowSupportWebchatParams): boolean => {
  if (isMounted || isExcludedApp) {
    return false
  }
  if (!whiteLabel.brandName) {
    return false
  }
  if (!resources.web_chat_support_script_url || !resources.web_chat_support_styles_url) {
    return false
  }

  const isPremiumChatGranted = getIsPremium(user, callcenter)

  // only the premium live chat needs the socket credentials; tickets are REST-only
  if (isPremiumChatGranted && (!resources.premium_support_wss_url || !user.premium_support_token)) {
    return false
  }

  return getIsTrial(user, callcenter) || isPremiumChatGranted || permissions.has("access_support_center")
}

export const mountSupportChat = ({ scriptUrl, stylesUrl, settings }: MountSupportChatPayload): void => {
  if (document.getElementById(SUPPORT_CHAT_IFRAME_ID)) {
    return
  }

  const iframe = document.createElement("iframe")

  iframe.id = SUPPORT_CHAT_IFRAME_ID
  iframe.setAttribute("frameborder", "no")

  iframe.onload = () => {
    const { contentDocument, contentWindow } = iframe

    if (!contentWindow || !contentDocument) {
      return
    }

    contentWindow.settings = settings

    const script = document.createElement("script")

    script.src = scriptUrl

    const link = document.createElement("link")

    link.rel = "stylesheet"
    link.href = stylesUrl

    contentDocument.head.appendChild(script)
    contentDocument.head.appendChild(link)
  }

  document.body.appendChild(iframe)
}
